import type { TimeSeriesPoint } from '../../services/types';

/**
 * Datenmodell der Dashboard-Charts: eine oder zwei benannte Serien mit
 * fester Farbe. Die Farbe folgt IMMER der Serie (fachliche Identitaet),
 * nie der Position im Chart (validierte Palette, Projekt-Design-Review).
 */

/** Serienfarbe 1: Blau (validierte Palette, Slot 1). */
export const SERIES_COLOR_BLUE = '#2a78d6';

/** Serienfarbe 2: Orange (validierte Palette, Slot 2). */
export const SERIES_COLOR_ORANGE = '#eb6834';

/** Erlaubte Serienfarben (nur die beiden Palette-Konstanten). */
export type SeriesColor = typeof SERIES_COLOR_BLUE | typeof SERIES_COLOR_ORANGE;

/**
 * Bekannte Serien-Schluessel (dataKey im Chart bzw. Spalten-Key in der
 * Tabelle). Als Union statt beliebigem string, damit "periodStart"
 * typseitig NICHT als Serien-Schluessel verwendbar ist und in SeriesRow
 * nicht mit der Perioden-Spalte kollidieren kann.
 *
 * - "value":     einzige Serie eines Ein-Serien-Charts
 * - "created":   angelegte Mietvertraege (Zwei-Serien-Chart)
 * - "finalized": finalisierte Mietvertraege (Zwei-Serien-Chart)
 */
export type SeriesKey = 'value' | 'created' | 'finalized';

/** Eine benannte Chart-Serie mit fester Farbe und Datenpunkten. */
export interface StatSeries {
  /** Technischer Schluessel der Serie (nie "periodStart", siehe SeriesKey). */
  readonly seriesKey: SeriesKey;
  /** Deutscher Anzeigename (Legende, Tabellen-Spalte, Tooltip). */
  readonly label: string;
  /** Feste Serienfarbe (SERIES_COLOR_BLUE / SERIES_COLOR_ORANGE). */
  readonly color: SeriesColor;
  /** Datenpunkte der Serie. */
  readonly points: readonly TimeSeriesPoint[];
}

/**
 * Eine zusammengefuehrte Datenzeile fuer Chart und Tabelle:
 * periodStart plus optional ein Wert je bekanntem seriesKey.
 */
export type SeriesRow = { readonly periodStart: string } & {
  readonly [K in SeriesKey]?: number;
};

/** Schreibbare Variante von SeriesRow (nur waehrend des Zusammenfuehrens). */
type MutableSeriesRow = { periodStart: string } & { [K in SeriesKey]?: number };

/** Optionen von mergeSeriesRows. */
export interface MergeSeriesOptions {
  /**
   * Fuellt fehlende Perioden einer Serie mit 0 auf. Fuer das
   * Zwei-Serien-Chart: recharts wuerde eine fehlende Periode als Luecke
   * zeichnen und im Tooltip komplett weglassen — nicht unterscheidbar von
   * "kein Wert". Das Backend fuellt Luecken bereits; die Normalisierung
   * ist rein defensiv.
   */
  readonly fillMissingWithZero?: boolean;
}

/**
 * Fuehrt die Datenpunkte mehrerer Serien anhand von periodStart zu
 * gemeinsamen Zeilen zusammen (Union aller Perioden, aufsteigend
 * sortiert). Fehlt einer Serie eine Periode, bleibt ihr Wert undefined —
 * es sei denn, fillMissingWithZero ist gesetzt.
 */
export function mergeSeriesRows(
  series: readonly StatSeries[],
  options?: MergeSeriesOptions
): SeriesRow[] {
  const rowsByPeriod = new Map<string, MutableSeriesRow>();
  for (const singleSeries of series) {
    for (const point of singleSeries.points) {
      let row = rowsByPeriod.get(point.periodStart);
      if (row === undefined) {
        row = { periodStart: point.periodStart };
        rowsByPeriod.set(point.periodStart, row);
      }
      row[singleSeries.seriesKey] = point.value;
    }
  }
  const rows = Array.from(rowsByPeriod.values()).sort((a, b) =>
    a.periodStart.localeCompare(b.periodStart)
  );
  if (options?.fillMissingWithZero === true) {
    for (const row of rows) {
      for (const singleSeries of series) {
        if (row[singleSeries.seriesKey] === undefined) {
          row[singleSeries.seriesKey] = 0;
        }
      }
    }
  }
  return rows;
}
